"use client"

import React, { useState, useMemo, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Breadcrumb from "../../utils/breadcrumbs"
import { FiEye, FiTrash2, FiMail } from "react-icons/fi"
import { ListLoader } from "../../utils/loader"
import ConfirmationModal from "../../ui/confirmation-modal"
import toast from "react-hot-toast"
import ReplyMessageOffcanvas from "../../components/contact-support/reply-message-offcanvas"
import Pagination from "../../utils/pagination2"
import api from "../../libs/axios"

export interface IContactMessage {
    id: number
    name: string
    email: string | null
    subject: string | null
    message: string
    status: "unread" | "read" | "replied"
    created_at: Date
    phone_number?: string | null
    image?: string | null
    replied_at?: Date | null
    replied_by?: number | null
    reply_message?: string | null
    updated_at?: Date
    user_id?: number | null
    user?: { id: number; phone_number?: string | null; name?: string | null } | null
    images?: string[] | null
}

const ContactMessages: React.FC = () => {
    const navigate = useNavigate()
    const [isReplyOpen, setIsReplyOpen] = useState<boolean>(false)
    const [selectedMessage, setSelectedMessage] = useState<IContactMessage | null>(null)
    const [searchParams, setSearchParams] = useState<Record<string, string>>({})
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [messages, setMessages] = useState<IContactMessage[]>([])
    const [loading, setLoading] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)

    // Pagination state variables
    const [currentPage, setCurrentPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    })

    // Filter messages based on search parameters
    const filteredMessages = useMemo(() => {
        return messages.filter((message) => {
            const nameMatch = !searchParams.name || message.name.toLowerCase().includes(searchParams.name.toLowerCase())

            const phoneMatch = !searchParams.phone || (message.phone_number && message.phone_number.toLowerCase().includes(searchParams.phone.toLowerCase()))

            const messageMatch =
                !searchParams.message || message.message.toLowerCase().includes(searchParams.message.toLowerCase())

            const statusMatch = !searchParams.status || message.status.toLowerCase() === searchParams.status.toLowerCase()

            return nameMatch && phoneMatch && messageMatch && statusMatch
        })
    }, [messages, searchParams])

    // Fetch messages with search params
    const fetchMessages = async (useSearchParams = false) => {
        setLoading(true)
        try {
            let url = `/contact-messages?page=${currentPage}&limit=${limit}`

            // Add search parameters to API call if they exist and useSearchParams is true
            if (useSearchParams && Object.keys(searchParams).length > 0) {
                const params = new URLSearchParams()
                if (searchParams.name) params.append("name", searchParams.name)
                if (searchParams.phone) params.append("phone", searchParams.phone)
                if (searchParams.message) params.append("message", searchParams.message)
                if (searchParams.status) params.append("status", searchParams.status)
                url += `&${params.toString()}`
            }

            const response = await api.get(url)
            setMessages(response.data.data)
            setPagination(response.data.pagination)
        } catch (error) {
            console.log(error)
            toast.error("Error fetching contact messages")
        } finally {
            setLoading(false)
        }
    }
    // Fetch messages on page/limit change
    useEffect(() => {
        fetchMessages()
    }, [currentPage, limit, searchParams?.status])

    // Search in database when filtered list is empty but search params exist
    useEffect(() => {
        const searchParamsKeys = Object.keys(searchParams)
        const hasSearchParams = searchParamsKeys.length > 0
        const hasMoreThanTwoChars = searchParamsKeys.some(key => searchParams[key].toString().length > 2)
        if (hasSearchParams && filteredMessages.length === 0 && !loading && hasMoreThanTwoChars) {
            fetchMessages(true)
        }
    }, [filteredMessages.length, searchParams])

    // Handle search
    const handleSearchChange = (column: string, value: string) => {
        setSearchParams((prev) => {
            const newParams = { ...prev }
            if (value) {
                newParams[column] = value
            } else {
                delete newParams[column]
            }
            return newParams
        })
        setCurrentPage(1)

        // If search is cleared (empty value), fetch fresh data from API
        if (!value) {
            fetchMessages(false)
        }
    }

    // Get status style
    const getStatusStyle = (status: string) => {
        switch (status.toLowerCase()) {
            case "read":
                return "bg-green-100 text-green-800"
            case "unread":
                return "bg-blue-100 text-blue-800"
            case "replied":
                return "bg-purple-100 text-purple-800"
            default:
                return "bg-gray-100 text-gray-800"
        }
    }

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    // Handle limit change
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit)
        setCurrentPage(1)
    }

    // Handle view message
    const handleViewMessage = (message: IContactMessage) => {
        setSelectedMessage(message)
        setIsReplyOpen(true)
    }

    // Handle delete
    const handleDeleteClick = (message: IContactMessage, e: React.MouseEvent) => {
        e.stopPropagation()
        setSelectedMessage(message)
        setDeleteConfirmOpen(true)
    }

    // Handle confirm delete
    const handleDeleteConfirm = async () => {
        if (selectedMessage) {
            setDeleteLoading(true)
            try {
                await api.delete(`/contact-messages/${selectedMessage.id}`)
                toast.success("Contact message deleted successfully")
                fetchMessages()
            } catch (err) {
                toast.error("Error deleting message")
            } finally {
                setDeleteLoading(false)
                setDeleteConfirmOpen(false)
                setSelectedMessage(null)
            }
        }
    }

    // Handle row click
    const handleRowClick = (message: IContactMessage) => {
        handleViewMessage(message)
    }

    return (
        <div className="w-full min-h-screen bg-white p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Contact Messages
                </h2>
                <Breadcrumb />
            </div>

            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-end p-4 w-full rounded-xl sticky top-0 bg-white">
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <FiMail className="w-4 h-4" />
                            <span>Total: {pagination.total}</span>
                        </div>
                        <div className="flex items-center gap-2 text-blue-600">
                            <FiMail className="w-4 h-4" />
                            <span>Unread: {messages.filter((m) => m.status === "unread").length}</span>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto min-h-[70vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-0">
                            <tr>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r w-[150px]">
                                    User
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r w-[200px]">
                                    Name
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r w-[180px]">
                                    Phone
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                                    Message
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r w-[120px]">
                                    Status
                                </th>
                                <th className="p-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r w-[100px]">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="bg-gray-50">
                                <td className="px-6 py-2 border-r"></td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Name"
                                        value={searchParams.name || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("name", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Phone"
                                        value={searchParams.phone || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("phone", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <input
                                        type="text"
                                        placeholder="Search Message"
                                        value={searchParams.message || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("message", e.target.value)}
                                    />
                                </td>
                                <td className="px-6 py-2 border-r">
                                    <select
                                        value={searchParams.status || ""}
                                        className="w-full p-2 h-10 border rounded text-sm"
                                        onChange={(e) => handleSearchChange("status", e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="unread">Unread</option>
                                        <option value="read">Read</option>
                                        <option value="replied">Replied</option>
                                    </select>
                                </td>
                                <td className="px-6 py-2 border-r"></td>
                            </tr>
                            {loading ? (
                                <ListLoader />
                            ) : filteredMessages.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-8 text-gray-500">
                                        No contact messages found
                                    </td>
                                </tr>
                            ) : (
                                filteredMessages.map((message) => (
                                    <tr
                                        key={message.id}
                                        className={`hover:bg-gray-50 cursor-pointer`}
                                        onClick={() => handleRowClick(message)}
                                    >
                                        <td className="px-6 py-4 border-r text-sm">
                                            {message.user_id ? (
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-primary-700">#{message.user_id}</span>
                                                    <span className="text-xs text-gray-500 truncate max-w-[130px]">{message.user?.name || message.user?.phone_number || ""}</span>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">Guest</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 border-r">
                                            <div className="flex font-semibold items-center">
                                                {message.status === "unread" && (
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0"></div>
                                                )}
                                                <span className="truncate">{message.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 border-r text-sm text-gray-600">
                                            <span className="truncate block">{message.phone_number || "N/A"}</span>
                                        </td>
                                        <td className="px-6 py-4 border-r">
                                            <div className="max-w-md truncate text-sm text-gray-700">{message.message}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                            <span
                                                className={`px-2 py-1 capitalize text-xs rounded-full ${getStatusStyle(
                                                    message.status
                                                )}`}
                                            >
                                                {message.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex space-x-3" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleViewMessage(message)
                                                    }}
                                                    className="text-primary-600 hover:text-primary-700"
                                                >
                                                    <FiEye className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={(e) => handleDeleteClick(message, e)}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex w-full bg-white sticky rounded-lg bottom-0">
                    <Pagination
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                </div>

                {deleteConfirmOpen && (
                    <ConfirmationModal
                        isOpen={deleteConfirmOpen}
                        onClose={() => setDeleteConfirmOpen(false)}
                        onConfirm={handleDeleteConfirm}
                        title="Delete Message"
                        description="Are you sure you want to delete this contact message? This action cannot be undone."
                        loading={deleteLoading}
                    />
                )}

                {/* Reply offcanvas */}
                {isReplyOpen && selectedMessage && (
                    <ReplyMessageOffcanvas
                        showReplyCanvas={isReplyOpen}
                        setShowReplyCanvas={setIsReplyOpen}
                        messageData={selectedMessage}
                        fetchMessages={fetchMessages}
                    />
                )}
            </div>
        </div>
    )
}

export default ContactMessages